import { YmmPicker, truckLabel } from '@theme/dialed-ymm';
import { getTruck } from '@theme/dialed-truck';

/**
 * <dialed-giveaway>: launch giveaway popup (sections/dialed-giveaway.liquid).
 *
 * Entries go to Klaviyo when the section has a public key: the email is subscribed to the entries
 * list with the entrant's referral code (and who referred them) on the profile, and a "Giveaway Entry"
 * event is tracked. Bonus entries track "Giveaway Bonus Entry" with the truck or phone; text alerts
 * also subscribe the phone to the SMS list. Without a key, entries fall back to Shopify's customer
 * form and bonus entries to the contact form.
 *
 * The entry count on screen is kept in this browser (localStorage). The official tally comes from
 * Klaviyo (or Shopify) data plus kit orders.
 */
const STORE = 'dialed:giveaway';
const REF = 'dialed:giveaway-ref';
const KLAVIYO = 'https://a.klaviyo.com/client';
const REVISION = '2025-01-15';

/** @returns {Record<string, any>} */
function load(key) {
  try {
    return JSON.parse(localStorage.getItem(key) ?? '{}') ?? {};
  } catch {
    return {};
  }
}

function save(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

/** Short, stable referral code from the email: 5 characters, no look-alikes */
function codeFor(email) {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let hash = 2166136261;
  for (const char of email.trim().toLowerCase()) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  let code = '';
  for (let i = 0; i < 5; i++) {
    code += alphabet[hash % alphabet.length];
    hash = Math.floor(hash / alphabet.length) || Math.imul(hash + i + 1, 2654435761) >>> 0;
  }
  return code;
}

/** US numbers to E.164 (+1XXXXXXXXXX); anything already starting with + is kept */
function e164(phone) {
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('+')) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return '';
}

class DialedGiveaway extends HTMLElement {
  #abort = new AbortController();
  /** @type {number | undefined} */
  #timer;
  /** @type {YmmPicker | null} */
  #picker = null;

  connectedCallback() {
    this.dialog = /** @type {HTMLDialogElement} */ (this.querySelector('dialog'));
    if (!this.dialog) return;
    this.end = Date.parse(this.dataset.end ?? '');
    this.monthEnd = this.dataset.monthly !== undefined ? Date.parse(this.dataset.monthEnd ?? '') : NaN;
    this.state = load(STORE);

    // A friend's link: remember who sent them and open the popup
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref && /^[A-Z0-9]{4,8}$/i.test(ref)) save(REF, { code: ref.toUpperCase(), at: Date.now() });

    // Back from a Shopify form post (the no-Klaviyo fallback)
    if (params.get('customer_posted') === 'true' && this.state.pending === 'entry') this.#entered(this.state.email);
    if (params.get('contact_posted') === 'true' && this.state.pending && this.state.pending !== 'entry') this.#bonusDone(this.state.pending);
    if (this.state.pending) {
      delete this.state.pending;
      save(STORE, this.state);
    }

    const { signal } = this.#abort;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('input', this.#onChange, { signal });
    this.querySelector('[data-gw-entry]')?.addEventListener('submit', this.#onEntry, { signal });
    // Close paths call close() directly; the dialog's own close event (Esc, form method=dialog) backs them up
    this.dialog.addEventListener('close', () => this.#onClose(), { signal });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    }, { signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    }, { signal });
    window.addEventListener('hashchange', () => this.#fromHash(), { signal });

    this.#render();
    this.#tick();
    this.#timer = window.setInterval(() => this.#tick(), 1000);

    const shouldOpen =
      this.#fromHash() ||
      Boolean(ref) ||
      params.has('customer_posted') ||
      params.has('contact_posted') ||
      this.querySelector('[data-gw-error]:not([hidden])');
    if (shouldOpen) this.open();
    else this.#autoOpen();
  }

  disconnectedCallback() {
    this.#abort.abort();
    window.clearInterval(this.#timer);
  }

  get closed() {
    return Number.isFinite(this.end) && Date.now() >= /** @type {number} */ (this.end);
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
    this.#render();
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
    this.#onClose();
  }

  #fromHash() {
    if (location.hash !== '#win') return false;
    this.open();
    return true;
  }

  #autoOpen() {
    if (this.dataset.autoOpen !== 'true' || this.hasAttribute('data-quiet') || this.closed || this.state.entered) return;
    if (this.state.snoozeUntil && Date.now() < this.state.snoozeUntil) return;
    const delay = (Number(this.dataset.delay) || 0) * 1000;
    // Open after the delay, but only once the visitor has scrolled, tapped or typed. Until then the
    // popup's big photo would count as the page's Largest Contentful Paint (and interrupt someone
    // who is still reading the first screen).
    const { signal } = this.#abort;
    let waited = false;
    let interacted = false;
    let done = false;
    const tryOpen = () => {
      if (done || !waited || !interacted) return;
      done = true;
      if (!document.querySelector('dialog[open]')) this.open();
    };
    const onInteract = () => {
      interacted = true;
      tryOpen();
    };
    for (const type of ['scroll', 'pointerdown', 'keydown']) {
      window.addEventListener(type, onInteract, { signal, once: true, passive: true });
    }
    window.setTimeout(() => {
      waited = true;
      tryOpen();
    }, delay);
  }

  #onClose() {
    if (!this.state.entered) {
      this.state.snoozeUntil = Date.now() + (Number(this.dataset.snoozeDays) || 3) * 86400000;
      save(STORE, this.state);
    }
    if (location.hash === '#win') history.replaceState(null, '', location.pathname + location.search);
    this.#render();
  }

  /* ---------- Render ---------- */

  #render() {
    const view = this.closed ? 'closed' : this.state.entered ? 'entered' : 'entry';
    if (this.dialog) this.dialog.dataset.state = view;
    for (const el of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-view]'))) {
      el.hidden = el.dataset.view !== view;
    }
    const launcher = /** @type {HTMLElement | null} */ (this.querySelector('.dl-gw__launcher'));
    if (launcher) launcher.hidden = this.closed || Boolean(this.dialog?.open) || this.hasAttribute('data-quiet');

    if (view !== 'entered') return;
    const done = this.state.done ?? {};
    let count = 1;
    if (done.truck) count += Number(this.dataset.ptsTruck) || 0;
    if (done.text) count += Number(this.dataset.ptsText) || 0;
    const countEl = this.querySelector('[data-gw-count]');
    if (countEl) countEl.textContent = String(count).padStart(2, '0');

    for (const kind of ['truck', 'text']) {
      const button = /** @type {HTMLButtonElement | null} */ (this.querySelector(`[data-gw-expand="${kind}"]`));
      if (!button) continue;
      button.toggleAttribute('data-done', Boolean(done[kind]));
      if (!done[kind]) button.textContent = this.dataset.tAdd ?? button.textContent;
      if (done[kind]) {
        button.textContent = this.dataset.tAdded ?? '';
        button.setAttribute('aria-expanded', 'false');
        const panel = this.querySelector(`#${button.getAttribute('aria-controls')}`);
        if (panel) /** @type {HTMLElement} */ (panel).hidden = true;
      }
    }
    const link = this.querySelector('[data-gw-link]');
    if (link) link.textContent = this.#shareLink().replace(/^https?:\/\//, '');
  }

  /**
   * When the countdown runs out: the end date, or with monthly drawings the end of this month's
   * entries. A cached page from last month rolls forward a month at a time.
   */
  #countdownEnd() {
    const end = /** @type {number} */ (this.end);
    if (!Number.isFinite(this.monthEnd)) return end;
    let monthEnd = /** @type {number} */ (this.monthEnd);
    while (monthEnd < Date.now()) {
      const next = new Date(monthEnd + 1000);
      monthEnd = new Date(next.getFullYear(), next.getMonth() + 1, 1).getTime() - 1000;
    }
    return Math.min(monthEnd, end);
  }

  #tick() {
    if (!Number.isFinite(this.end)) return;
    const left = Math.max(0, this.#countdownEnd() - Date.now());
    const parts = {
      days: Math.floor(left / 86400000),
      hours: Math.floor(left / 3600000) % 24,
      minutes: Math.floor(left / 60000) % 60,
      seconds: Math.floor(left / 1000) % 60,
    };
    for (const [key, value] of Object.entries(parts)) {
      const el = this.querySelector(`[data-clock="${key}"]`);
      if (el) el.textContent = String(value).padStart(2, '0');
    }
    if (!left && this.dialog?.dataset.state !== 'closed') this.#render();
  }

  #shareLink() {
    const base = this.dataset.shareBase ?? `${location.origin}/`;
    return `${base}?ref=${this.state.code ?? ''}`;
  }

  /* ---------- Entry ---------- */

  /** @param {SubmitEvent} event */
  #onEntry = async (event) => {
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const email = /** @type {HTMLInputElement} */ (form.querySelector('input[type="email"]')).value.trim();
    if (!email) return;
    const code = codeFor(email);
    const referrer = load(REF).code ?? '';

    if (!this.#klaviyo) {
      // Shopify fallback: tag the customer, then let the form post and come back
      const tags = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-gw-tags]'));
      if (tags) tags.value = [tags.value, `code-${code}`, referrer && `ref-${referrer}`].filter(Boolean).join(', ');
      Object.assign(this.state, { email, code, pending: 'entry' });
      save(STORE, this.state);
      return;
    }

    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    button?.setAttribute('aria-busy', 'true');
    const ok = await this.#subscribe({
      list: this.#klaviyo.list,
      profile: {
        email,
        properties: {
          giveaway_code: code,
          ...(referrer && referrer !== code ? { giveaway_referred_by: referrer } : {}),
          giveaway_entered_at: new Date().toISOString(),
        },
        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
      },
    });
    if (ok) await this.#track('Giveaway Entry', email, { code, referred_by: referrer || undefined, source: 'popup' });
    button?.removeAttribute('aria-busy');
    if (!ok) return this.#error(form);
    this.#entered(email);
  };

  /** @param {string} email */
  #entered(email) {
    Object.assign(this.state, { entered: true, email, code: codeFor(email ?? ''), done: this.state.done ?? {} });
    delete this.state.snoozeUntil;
    save(STORE, this.state);
    this.#render();
    this.querySelector('[data-gw-count]')?.closest('.dl-gw__view')?.querySelector('h2')?.focus?.();
  }

  /** @param {Element} scope */
  #error(scope) {
    const error = /** @type {HTMLElement | null} */ (scope.querySelector('[data-gw-error]') ?? this.querySelector('[data-gw-error]'));
    if (!error) return;
    error.textContent = this.dataset.tError ?? '';
    error.hidden = false;
  }

  /* ---------- Bonus entries ---------- */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-gw-open]')) return this.open();
    if (target.closest('[data-gw-close]')) return this.close();

    const expand = target.closest('[data-gw-expand]');
    if (expand instanceof HTMLButtonElement && !expand.hasAttribute('data-done')) {
      const panel = /** @type {HTMLElement | null} */ (this.querySelector(`#${expand.getAttribute('aria-controls')}`));
      const opening = expand.getAttribute('aria-expanded') !== 'true';
      expand.setAttribute('aria-expanded', String(opening));
      if (panel) panel.hidden = !opening;
      if (opening && expand.dataset.gwExpand === 'truck') this.#setupPicker();
      if (opening) /** @type {HTMLElement | null} */ (panel?.querySelector('select, input'))?.focus();
      return;
    }

    const submit = target.closest('[data-gw-submit]');
    if (submit instanceof HTMLButtonElement) return void this.#bonus(/** @type {string} */ (submit.dataset.gwSubmit), submit);

    const copy = target.closest('[data-gw-copy]');
    if (copy instanceof HTMLButtonElement) {
      const link = this.#shareLink();
      const done = () => {
        copy.textContent = this.dataset.tCopied ?? '';
        window.setTimeout(() => (copy.textContent = this.dataset.tCopy ?? ''), 2000);
      };
      if (navigator.share && matchMedia('(pointer: coarse)').matches) {
        navigator.share({ url: link }).catch(() => {});
      } else {
        navigator.clipboard?.writeText(link).then(done, () => window.prompt('', link));
      }
    }
  };

  #onChange = () => {
    const truckReady = Boolean(this.#picker?.fit()) || Boolean(this.#value('[data-gw-truck-other]'));
    const truckBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-gw-submit="truck"]'));
    if (truckBtn) truckBtn.disabled = !truckReady;
    const consent = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-gw-consent]'));
    const textBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-gw-submit="text"]'));
    if (textBtn) textBtn.disabled = !(e164(this.#value('[data-gw-phone]')) && consent?.checked);
  };

  #setupPicker() {
    if (this.#picker) return;
    let fits = [];
    try {
      fits = JSON.parse(this.querySelector('[data-dialed-trucks]')?.textContent ?? '[]');
    } catch {}
    const get = (name) => /** @type {HTMLSelectElement | null} */ (this.querySelector(`[data-gw-ymm="${name}"]`));
    const [year, make, model, bed] = ['year', 'make', 'model', 'bed'].map(get);
    if (!fits.length || !year || !make || !model || !bed) return;
    this.#picker = new YmmPicker(fits, { year, make, model, bed });
    this.#picker.bind(() => this.#onChange());
    const stored = getTruck();
    this.#picker.set(stored ? { year: stored.year, make: stored.make, model: stored.model, fit: stored.fit } : {});
    this.#onChange();
  }

  /** @param {string} selector */
  #value(selector) {
    return /** @type {HTMLInputElement | null} */ (this.querySelector(selector))?.value.trim() ?? '';
  }

  /**
   * @param {string} kind
   * @param {HTMLButtonElement} button
   */
  async #bonus(kind, button) {
    const email = this.state.email;
    if (!email) return;
    const fit = this.#picker?.fit();
    const truck = kind === 'truck' ? (fit ? truckLabel(fit, this.#picker?.state.year ?? '') : this.#value('[data-gw-truck-other]')) : '';
    const phone = kind === 'text' ? e164(this.#value('[data-gw-phone]')) : '';

    if (!this.#klaviyo) {
      // Shopify fallback: the contact form emails the store, then the page comes back
      const form = /** @type {HTMLFormElement | null} */ (this.querySelector('[data-gw-extra]'));
      if (!form) return;
      const set = (key, value) => {
        const input = /** @type {HTMLInputElement | null} */ (form.querySelector(`[data-gw-x="${key}"]`));
        if (input) input.value = value;
      };
      set('email', email);
      set('kind', kind === 'truck' ? 'Bonus entry: truck' : 'Bonus entry: text alerts');
      set('code', this.state.code ?? '');
      set('truck', truck);
      set('phone', phone);
      set('consent', kind === 'text' ? 'Yes' : '');
      this.state.pending = kind;
      save(STORE, this.state);
      form.submit();
      return;
    }

    button.setAttribute('aria-busy', 'true');
    let ok = true;
    if (kind === 'text' && this.#klaviyo.smsList) {
      ok = await this.#subscribe({
        list: this.#klaviyo.smsList,
        profile: {
          email,
          phone_number: phone,
          subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } },
        },
      });
    }
    if (ok) {
      ok = await this.#track('Giveaway Bonus Entry', email, { kind, truck: truck || undefined, phone: phone || undefined, code: this.state.code }, {
        ...(truck ? { giveaway_truck: truck } : {}),
        ...(phone ? { giveaway_text_alerts: true } : {}),
      });
    }
    button.removeAttribute('aria-busy');
    if (!ok) return this.#error(button.closest('.dl-gw__expand') ?? this);
    this.#bonusDone(kind);
  }

  /** @param {string} kind */
  #bonusDone(kind) {
    this.state.done = { ...(this.state.done ?? {}), [kind]: true };
    save(STORE, this.state);
    this.#render();
  }

  /* ---------- Klaviyo client API ---------- */

  get #klaviyo() {
    const key = this.dataset.klKey;
    const list = this.dataset.klList;
    return key && list ? { key, list, smsList: this.dataset.klSmsList ?? '' } : null;
  }

  /**
   * @param {string} path
   * @param {object} body
   */
  async #post(path, body) {
    const klaviyo = this.#klaviyo;
    if (!klaviyo) return false;
    try {
      const response = await fetch(`${KLAVIYO}/${path}/?company_id=${encodeURIComponent(klaviyo.key)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: REVISION },
        body: JSON.stringify(body),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /** @param {{ list: string, profile: Record<string, any> }} input */
  #subscribe({ list, profile }) {
    return this.#post('subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: this.dataset.klSource || 'Giveaway popup',
          profile: { data: { type: 'profile', attributes: profile } },
        },
        relationships: { list: { data: { type: 'list', id: list } } },
      },
    });
  }

  /**
   * @param {string} metric
   * @param {string} email
   * @param {Record<string, any>} properties
   * @param {Record<string, any>} [profileProperties]
   */
  #track(metric, email, properties, profileProperties = {}) {
    return this.#post('events', {
      data: {
        type: 'event',
        attributes: {
          properties: JSON.parse(JSON.stringify(properties)),
          metric: { data: { type: 'metric', attributes: { name: metric } } },
          profile: { data: { type: 'profile', attributes: { email, properties: profileProperties } } },
        },
      },
    });
  }
}

if (!customElements.get('dialed-giveaway')) customElements.define('dialed-giveaway', DialedGiveaway);
